import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import styles from './subbidform.module.css';
import modalStyles from '../../styles/modal.module.css';
import { getContacts } from '../../services/api';
import { useAuth } from '../../contexts/authcontext';

// List of standard trades
const STANDARD_TRADES = [
  "Concrete",
  "Demolition",
  "Drywall",
  "Electrical",
  "Excavation",
  "Fire Protection",
  "Flooring",
  "Framing",
  "HVAC",
  "Insulation",
  "Masonry",
  "Painting",
  "Plumbing",
  "Roofing",
  "Steel"
];

function SubBidForm({ onSubmit, onCancel, defaultValues }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddressBook, setShowAddressBook] = useState(false);
  const [subcontractors, setSubcontractors] = useState([]);
  const [isLoadingSubcontractors, setIsLoadingSubcontractors] = useState(false);
  const [filters, setFilters] = useState({ search: '', trade: '' });
  const { user } = useAuth();
  
  // Check if the default Trade is a non-standard one
  const isNonStandardTrade = defaultValues?.Trade && !STANDARD_TRADES.includes(defaultValues.Trade);
  
  // Prepare default values - if the trade is non-standard, set Trade to "Other" and use CustomTrade
  const formDefaultValues = {
    SubcontractorName: defaultValues?.SubcontractorName || '',
    Trade: isNonStandardTrade ? 'Other' : (defaultValues?.Trade || ''),
    CustomTrade: isNonStandardTrade ? defaultValues?.Trade : '',
    Contact: defaultValues?.Contact || '',
    SubBidAmount: defaultValues?.SubBidAmount || '',
    AwardedAmount: defaultValues?.AwardedAmount || '0',
  };
  
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    defaultValues: formDefaultValues
  });

  // Fetch subcontractors when modal is opened
  useEffect(() => {
    if (showAddressBook && user) {
      const fetchSubcontractors = async () => {
        try {
          setIsLoadingSubcontractors(true);
          const fetchedSubcontractors = await getContacts(user.uid, 'subcontractor');
          setSubcontractors(fetchedSubcontractors);
        } catch (error) {
          console.error('SubBidForm: Error fetching subcontractors:', error);
        } finally {
          setIsLoadingSubcontractors(false);
        }
      };
      
      fetchSubcontractors();
    }
  }, [showAddressBook, user]);

  // Get unique trades for filter dropdown
  const uniqueTrades = useMemo(() => {
    const trades = subcontractors
      .map(sub => sub.trade)
      .filter(Boolean) // Remove undefined/null values
      .sort();
    
    // Remove duplicates
    return [...new Set(trades)];
  }, [subcontractors]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters to subcontractors
  const filteredSubcontractors = useMemo(() => {
    // Sort subcontractors by name (case-insensitive)
    const sortedSubs = [...subcontractors].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return sortedSubs.filter(sub => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const subValues = [
          sub.name, 
          sub.trade, 
          sub.email, 
          sub.phone
        ].filter(Boolean).map(val => val.toLowerCase());
        const matchesSearch = subValues.some(val => val.includes(searchTerm));
        if (!matchesSearch) return false;
      }
      // Trade filter
      if (filters.trade && sub.trade !== filters.trade) {
        return false;
      }
      return true;
    });
  }, [subcontractors, filters]);

  // Handle selecting a subcontractor from the modal
  const handleSelectSubcontractor = (subcontractor) => {
    // Determine if the trade is standard or custom
    const isStandardTrade = STANDARD_TRADES.includes(subcontractor.trade);
    
    // Set the Trade field
    setValue('Trade', isStandardTrade ? subcontractor.trade : 'Other');
    
    // Set CustomTrade if needed
    if (!isStandardTrade) {
      setValue('CustomTrade', subcontractor.trade);
    }
    
    // Set Subcontractor Name and Contact
    setValue('SubcontractorName', subcontractor.name);
    setValue('Contact', subcontractor.email || subcontractor.phone || '');
    
    // Close the modal
    setShowAddressBook(false);
    
    // Reset filters for next time
    setFilters({ search: '', trade: '' });
  };

  // Watch the Trade field to handle custom trade logic
  const selectedTrade = watch('Trade');
  const [trade, setTrade] = useState(formDefaultValues.Trade);
  
  // Update local state when trade changes
  useEffect(() => {
    setTrade(selectedTrade);
  }, [selectedTrade]);

  const onFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Convert amounts to numbers
      const formattedData = {
        ...data,
        SubBidAmount: parseFloat(data.SubBidAmount) || 0,
        AwardedAmount: parseFloat(data.AwardedAmount) || 0,
        // If "Other" is selected, use the custom trade value
        Trade: data.Trade === 'Other' ? data.CustomTrade : data.Trade,
      };
      
      // Remove CustomTrade field if it exists to keep data clean
      if (formattedData.CustomTrade) {
        delete formattedData.CustomTrade;
      }
      
      console.log('SubBidForm: Submitting form with data:', formattedData);
      await onSubmit(formattedData);
    } catch (error) {
      console.error('SubBidForm: Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={modalStyles.modalContent}>
      <button 
        type="button" 
        onClick={() => setShowAddressBook(true)} 
        className={modalStyles.secondaryButton}
        style={{ marginBottom: '16px' }}
      >
        Select Subcontractor
      </button>
      
      <form onSubmit={handleSubmit(onFormSubmit)} className={styles.form}>
        <div className={modalStyles.formGroup}>
          <label>Trade</label>
          <select 
            {...register('Trade', { required: 'Trade is required' })}
          >
            <option value="">Select Trade</option>
            {STANDARD_TRADES.map(trade => (
              <option key={trade} value={trade}>{trade}</option>
            ))}
            <option value="Other">Other</option>
          </select>
          {errors.Trade && <span className={styles.error}>{errors.Trade.message}</span>}
        </div>
        
        {trade === 'Other' && (
          <div className={modalStyles.formGroup}>
            <label>Custom Trade</label>
            <input 
              {...register('CustomTrade', { 
                required: trade === 'Other' ? 'Custom trade is required' : false 
              })} 
              placeholder="Enter custom trade" 
            />
            {errors.CustomTrade && <span className={styles.error}>{errors.CustomTrade.message}</span>}
          </div>
        )}
        
        <div className={modalStyles.formGroup}>
          <label>Subcontractor Name</label>
          <input 
            {...register('SubcontractorName', { required: 'Subcontractor Name is required' })} 
            placeholder="e.g., Subcontractor A" 
          />
          {errors.SubcontractorName && <span className={styles.error}>{errors.SubcontractorName.message}</span>}
        </div>
        
        <div className={modalStyles.formGroup}>
          <label>Bid Amount</label>
          <input 
            type="number" 
            {...register('SubBidAmount', { 
              required: 'Bid Amount is required', 
              min: { value: 0, message: 'Amount must be positive' } 
            })} 
            placeholder="e.g., 10000" 
          />
          {errors.SubBidAmount && <span className={styles.error}>{errors.SubBidAmount.message}</span>}
        </div>
        
        <div className={modalStyles.formGroup}>
          <label>Awarded Amount</label>
          <input 
            type="number" 
            {...register('AwardedAmount', { 
              min: { value: 0, message: 'Amount must be positive' } 
            })} 
            placeholder="e.g., 0" 
          />
          {errors.AwardedAmount && <span className={styles.error}>{errors.AwardedAmount.message}</span>}
        </div>
        
        <div className={modalStyles.formGroup}>
          <label>Contact</label>
          <input 
            {...register('Contact')} 
            placeholder="e.g., John Doe (555-123-4567)" 
          />
          {errors.Contact && <span className={styles.error}>{errors.Contact.message}</span>}
        </div>
        
        <div className={modalStyles.formActions}>
          <button 
            type="submit" 
            className={modalStyles.primaryButton}
            disabled={isSubmitting}
          >
            {isSubmitting 
              ? (defaultValues ? 'Updating...' : 'Creating...') 
              : (defaultValues ? 'Update Sub-Bid' : 'Create Sub-Bid')
            }
          </button>
          <button 
            type="button" 
            onClick={onCancel} 
            className={modalStyles.secondaryButton}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </form>
      
      {/* Subcontractor Address Book Modal */}
      {showAddressBook && (
        <div className={modalStyles.modalOverlay}>
          <div className={modalStyles.modalContent}>
            {/* X Close Button at top right */}
            <button
              type="button"
              onClick={() => {
                setShowAddressBook(false);
                setFilters({ search: '', trade: '' });
              }}
              className={modalStyles.closeButton}
              aria-label="Close"
              style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', color: 'white', cursor: 'pointer', zIndex: 2 }}
            >
              &times;
            </button>
            <h3 className={styles.modalTitle}>Subcontractor Address Book</h3>
            {/* Filters for the subcontractor list */}
            <div className={modalStyles.formGroup}>
              {/* Search Bar */}
              <input 
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search subcontractors..."
                className={modalStyles.inputField}
              />
              {/* Trade Filter Dropdown */}
              <select
                name="trade"
                value={filters.trade}
                onChange={handleFilterChange}
                className={modalStyles.inputField}
                aria-label="Filter by Trade"
              >
                <option value="">All Trades</option>
                {uniqueTrades.map(trade => (
                  <option key={trade} value={trade}>{trade}</option>
                ))}
              </select>
            </div>
            {isLoadingSubcontractors ? (
              <div className={styles.loading}>Loading subcontractors...</div>
            ) : subcontractors.length === 0 ? (
              <div className={styles.empty}>No subcontractors found.</div>
            ) : filteredSubcontractors.length === 0 ? (
              <div className={styles.empty}>No subcontractors match your filters.</div>
            ) : (
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Trade</th>
                      <th>Subcontractor Name</th>
                      <th>Contact</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubcontractors.map(sub => (
                      <tr key={sub.id}>
                        <td>{sub.trade || '-'}</td>
                        <td>{sub.name || '-'}</td>
                        <td>{sub.email || sub.phone || '-'}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() => handleSelectSubcontractor(sub)}
                            className={modalStyles.secondaryButton}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SubBidForm; 